"use client";

import { c as _c } from "react/compiler-runtime";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useMemo } from "react";
import Thumbnail from "../thumbnail";
import MoneyClient from "../../../../components/MoneyClient";
import { normalizeCurrency, toCents } from "@lib/currency";
import { STORE_BASE_CURRENCY } from "@lib/config";
import { resolveInventoryAvailability } from "./stock";
export { extractVariantOptionValues } from "@lib/util/variant-options";
export type QuickAddDrawerProps = {
  product: any;
  open: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onSelectVariant: (variant: any) => void;
};
export const getVariantLabel = (product: any, variant: any) => {
  if (!variant) return "";
  const productOptions: any[] = Array.isArray(product?.options) ? product.options : [];
  const optionValues = variant?.optionValues || {};
  const fromOptionValues = productOptions.map(opt => {
    const key = opt.name || opt.title || opt.id;
    return optionValues?.[opt.name] || optionValues?.[opt.title] || optionValues?.[key];
  }).filter(val => typeof val === "string" && val.length > 0);
  if (fromOptionValues.length) {
    return fromOptionValues.join(" / ");
  }
  if (Array.isArray(variant?.options)) {
    const labels = variant.options.map((opt: any) => opt?.option_value || opt?.value).filter(Boolean);
    if (labels.length) {
      return labels.join(" / ");
    }
  }
  return variant?.title || variant?.sku || "Variante";
};
export const VariantAvailabilityLabel = t0 => {
  const $ = _c(7);
  const {
    product,
    variant
  } = t0;
  const track = typeof variant?.trackInventory === "boolean" ? variant.trackInventory : product?.trackInventory;
  const stock = variant?.stock ?? variant?.inventory_quantity;
  const t1 = variant?.inventoryLevels;
  let t2;
  if ($[0] !== stock || $[1] !== t1 || $[2] !== track) {
    t2 = resolveInventoryAvailability({
      trackInventory: track,
      stock,
      inventoryLevels: t1
    });
    $[0] = stock;
    $[1] = t1;
    $[2] = track;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const availability = t2;
  const t3 = `text-xs ${availability.inStock ? "text-gray-500" : "text-rose-600"}`;
  let t4;
  if ($[4] !== availability.label || $[5] !== t3) {
    t4 = <span className={t3}>{availability.label}</span>;
    $[4] = availability.label;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
export default function QuickAddDrawer({
  product,
  open,
  isLoading,
  onClose,
  onSelectVariant
}: QuickAddDrawerProps) {
  const variants: any[] = useMemo(() => Array.isArray(product?.variants) ? product.variants : [], [product?.variants]);
  const productCurrency = normalizeCurrency(typeof product?.currency === "string" ? product.currency : STORE_BASE_CURRENCY, STORE_BASE_CURRENCY);
  return <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[75]" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <Transition.Child as={Fragment} enter="transform transition ease-out duration-200" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transform transition ease-in duration-150" leaveFrom="translate-x-0" leaveTo="translate-x-full">
            <Dialog.Panel className="pointer-events-auto flex h-full w-screen max-w-md flex-col bg-white shadow-2xl">
              <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
                <div>
                  <Dialog.Title className="text-base font-semibold text-gray-900">
                    Selecciona una opción
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-500">
                    Elige la variante que deseas agregar a tu carrito.
                  </p>
                </div>
                <button type="button" onClick={onClose} className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700" aria-label="Cerrar selector de variantes">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center gap-4 px-6 py-5">
                <div className="h-20 w-20 overflow-hidden rounded-lg border border-gray-100">
                  <Thumbnail thumbnail={product?.thumbnail} videoUrl={product?.videoUrl} images={product?.images} size="square" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{product?.name}</h3>
                  {typeof product?.price === "number" ? <p className="mt-1 text-sm font-semibold text-gray-900">
                      <MoneyClient amountCents={toCents(product.price)} fromCurrency={productCurrency} />
                    </p> : null}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="grid grid-cols-1 gap-3">
                  {variants.map((variant: any) => {
                  const label = getVariantLabel(product, variant);
                  const track = typeof variant?.trackInventory === "boolean" ? variant.trackInventory : product?.trackInventory;
                  const stock = variant?.stock ?? variant?.inventory_quantity;
                  const availability = resolveInventoryAvailability({
                    trackInventory: track,
                    stock,
                    inventoryLevels: variant?.inventoryLevels
                  });
                  const available = availability.inStock;
                  const price = variant?.price ?? variant?.calculated_price ?? product?.price ?? 0;
                  return <button key={variant?.id || variant?._id || variant?.sku || label} type="button" onClick={() => onSelectVariant(variant)} disabled={!available || isLoading} className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition ${available ? "border-gray-200 hover:border-gray-900" : "border-gray-100 bg-gray-50 text-gray-400"} ${isLoading ? "pointer-events-none opacity-60" : ""}`}>
                        <span className="flex min-w-0 flex-col pr-4">
                          <span className="truncate text-sm font-medium">{label}</span>
                          <VariantAvailabilityLabel product={product} variant={variant} />
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          <MoneyClient amountCents={toCents(price)} fromCurrency={productCurrency} />
                        </span>
                      </button>;
                })}
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>;
}